import './utils/disableConsoleLogs';
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApolloProvider } from '@apollo/client/react';
import { ToastProvider } from '@crisellevaldez/speculo-ui';
import { GlobalEmailPreviewModal } from './components/common/GlobalEmailPreviewModal';
import { apolloClient } from './lib/apolloClient';
import { isProduction } from './config/environment';
import { TrackingParamsSync } from './components/tracking/TrackingParamsSync';
import Contact from './pages/Contacts';
import CallsDashboard from './pages/CallsDashboard';
import DashboardV2 from './pages/dashboard/v2';
import TestDataGenerator from './pages/test-data-generator';
import TestDataGeneratorProperty from './pages/test-data-generator-property';
import ContactActivityGenerator from './pages/contact-activity-generator';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { ClientOnboarding } from './pages/ClientOnboarding';
import Flows from './pages/Flows';
import ClientAdmin from './pages/ClientAdmin';
// import MyListings from './pages/MyListings';
import DebugPage from './pages/DebugPage';
import ContactEditPage from './components/features/contacts/edit';
import ContactPreviewPage from './pages/contact-preview';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { PageLayout } from './components/common/layout/PageLayout';
import { AuthProvider } from './contexts/auth/AuthProvider';
import {
  CallHandoffPage,
  NotesHandoffPage,
  DeclineHandoffPage,
  HandoffSuccessPage,
  HandoffErrorPage,
} from './pages/handoff';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App: React.FC = () => {
  return (
    <ApolloProvider client={apolloClient}>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider>
            <BrowserRouter>
              <TrackingParamsSync />
              <Routes>
                {/* Redirect root to dashboard */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />

                {/* Auth pages */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                {/* Client Onboarding */}
                <Route
                  path="/onboarding"
                  element={
                    <ProtectedRoute>
                      <ClientOnboarding />
                    </ProtectedRoute>
                  }
                />

                {/* Protected routes */}
                <Route
                  path="/contacts"
                  element={
                    <ProtectedRoute>
                      <PageLayout title="Contacts">
                        <Contact />
                      </PageLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <PageLayout title="Dashboard" disablePadding>
                        <DashboardV2 />
                      </PageLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Handoff routes */}
                <Route
                  path="/handoff/call/:token"
                  element={
                    <ProtectedRoute>
                      <CallHandoffPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/handoff/notes/:token"
                  element={
                    <ProtectedRoute>
                      <NotesHandoffPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/handoff/decline/:token"
                  element={
                    <ProtectedRoute>
                      <DeclineHandoffPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/handoff/success"
                  element={
                    <ProtectedRoute>
                      <HandoffSuccessPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/handoff/error"
                  element={
                    <ProtectedRoute>
                      <HandoffErrorPage />
                    </ProtectedRoute>
                  }
                />
                {/* Local visual QA — fully populated mock contact, no live data.
                    disablePadding lets the amber bg extend up under the
                    fixed header; the page reapplies the offset internally. */}
                <Route
                  path="/contact-preview"
                  element={
                    <ProtectedRoute>
                      <PageLayout title="Contact Preview" disablePadding>
                        <ContactPreviewPage />
                      </PageLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Contact routes with different parameter combinations.
                    Plural `/contacts/...` aliases support legacy URLs that
                    shipped before we settled on the singular canonical form
                    — same handler, same params, no redirect so external
                    links still work. */}
                {/* Routes with organization ID */}
                <Route
                  path="/contact/:orgId/:email/:phone"
                  element={
                    <ProtectedRoute>
                      <PageLayout title="Edit Contact">
                        <ContactEditPage />
                      </PageLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contacts/:orgId/:email/:phone"
                  element={
                    <ProtectedRoute>
                      <PageLayout title="Edit Contact">
                        <ContactEditPage />
                      </PageLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contact/:orgId/:email"
                  element={
                    <ProtectedRoute>
                      <PageLayout title="Edit Contact">
                        <ContactEditPage />
                      </PageLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contacts/:orgId/:email"
                  element={
                    <ProtectedRoute>
                      <PageLayout title="Edit Contact">
                        <ContactEditPage />
                      </PageLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contact/:orgId/:phone"
                  element={
                    <ProtectedRoute>
                      <PageLayout title="Edit Contact">
                        <ContactEditPage />
                      </PageLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contacts/:orgId/:phone"
                  element={
                    <ProtectedRoute>
                      <PageLayout title="Edit Contact">
                        <ContactEditPage />
                      </PageLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/contact/:contactId"
                  element={
                    <ProtectedRoute>
                      <PageLayout title="Edit Contact">
                        <ContactEditPage />
                      </PageLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contacts/:contactId"
                  element={
                    <ProtectedRoute>
                      <PageLayout title="Edit Contact">
                        <ContactEditPage />
                      </PageLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calls"
                  element={
                    <ProtectedRoute>
                      <PageLayout title="Calls">
                        <CallsDashboard />
                      </PageLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/flows"
                  element={
                    <ProtectedRoute>
                      <Flows />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client-administration"
                  element={
                    <ProtectedRoute requiredRole="super_admin">
                      <PageLayout title="Client Administration">
                        <ClientAdmin />
                      </PageLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Catch all - redirect to dashboard */}
                {/* <Route
                path="/my-listings"
                element={
                  <ProtectedRoute>
                    <PageLayout title="My Listings">
                      <MyListings />
                    </PageLayout>
                  </ProtectedRoute>
                }
              /> */}

                {/* Debug route */}
                <Route
                  path="/debug"
                  element={
                    <ProtectedRoute>
                      <PageLayout title="Debug">
                        <DebugPage />
                      </PageLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Test Data Generators - Only available in non-production environments */}
                {!isProduction() && (
                  <>
                    <Route
                      path="/test-data-generator"
                      element={
                        <ProtectedRoute>
                          <PageLayout title="Test Data Generator">
                            <TestDataGenerator />
                          </PageLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/test-data-generator-property"
                      element={
                        <ProtectedRoute>
                          <PageLayout title="Property Insights Test Data Generator">
                            <TestDataGeneratorProperty />
                          </PageLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/contact-activity-generator"
                      element={
                        <ProtectedRoute>
                          <PageLayout title="Contact Activity Generator">
                            <ContactActivityGenerator />
                          </PageLayout>
                        </ProtectedRoute>
                      }
                    />
                  </>
                )}

                {/* Catch all - redirect to dashboard */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
              <GlobalEmailPreviewModal />
            </BrowserRouter>
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ApolloProvider>
  );
};

export default App;
